import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Users, ChevronLeft, Eye, EyeOff, ToggleLeft, ToggleRight, Loader } from "lucide-react";
import api from "../services/api";
import { toast } from "react-toastify";

const EMPTY_FORM = { email: "", password: "", businessName: "", phone: "" };

const AgentManagement = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/agents");
      setAgents(r.data.agents || []);
    } catch {
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAgents(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/users/agents", {
        email: form.email,
        password: form.password,
        businessName: form.businessName || undefined,
        phone: form.phone || undefined,
      });
      setAgents((prev) => [r.data.agent, ...prev]);
      setForm(EMPTY_FORM);
      setShowForm(false);
      toast.success("Agent created successfully");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to create agent");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (agent) => {
    setTogglingId(agent.id);
    try {
      const r = await api.put(`/users/agents/${agent.id}/status`, {
        status: !agent.status,
      });
      setAgents((prev) =>
        prev.map((a) => (a.id === agent.id ? { ...a, status: r.data.agent.status } : a))
      );
      toast.success(`Agent ${r.data.agent.status ? "activated" : "deactivated"}`);
    } catch {
      toast.error("Failed to update agent status");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Agent Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Create and manage agents for your workspace</p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm((v) => !v); setForm(EMPTY_FORM); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          New Agent
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Create New Agent</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="agent@example.com"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Min 8 characters"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name <span className="text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  value={form.businessName}
                  onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
                  placeholder="Agent name"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone <span className="text-gray-400">(optional)</span></label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+91..."
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
              >
                {submitting && <Loader className="w-3.5 h-3.5 animate-spin" />}
                Create Agent
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Agents list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-700">
            Agents {!loading && <span className="text-gray-400 font-normal">({agents.length})</span>}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-400">No agents yet</p>
            <p className="text-xs text-gray-400 mt-1">Click "New Agent" to create one</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {agents.map((agent) => (
              <li key={agent.id} className="flex items-center justify-between px-5 py-3.5">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {agent.businessName || <span className="text-gray-400 italic">No name</span>}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{agent.email}</p>
                  {agent.phone && <p className="text-xs text-gray-400">{agent.phone}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      agent.status
                        ? "bg-green-50 text-green-600"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {agent.status ? "Active" : "Inactive"}
                  </span>
                  <button
                    onClick={() => handleToggleStatus(agent)}
                    disabled={togglingId === agent.id}
                    className="text-gray-400 hover:text-blue-500 disabled:opacity-40 transition-colors"
                    title={agent.status ? "Deactivate" : "Activate"}
                  >
                    {togglingId === agent.id ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : agent.status ? (
                      <ToggleRight className="w-6 h-6 text-green-500" />
                    ) : (
                      <ToggleLeft className="w-6 h-6" />
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AgentManagement;
